import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Star, Loader2, MessageSquare, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUserAuth } from '../../contexts/UserAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Star components ───────────────────────────────────────────────────────────
function StarDisplay({ value, size = 'md' }) {
  const cls = size === 'sm' ? 'w-3.5 h-3.5' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  return (
    <span className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`${cls} transition-colors ${i <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-slate-700 fill-slate-700'}`} />
      ))}
    </span>
  );
}

function StarPicker({ value, onChange, label }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-400 w-28 shrink-0">{label}</span>
      <div className="flex items-center gap-1">
        {[1,2,3,4,5].map(i => (
          <button key={i} type="button"
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(i)}
            className="p-0.5 transition-transform hover:scale-110 focus:outline-none"
          >
            <Star className={`w-5 h-5 transition-colors ${i <= (hovered || value) ? 'text-amber-400 fill-amber-400' : 'text-slate-700 fill-slate-700'}`} />
          </button>
        ))}
        {value > 0 && <span className="text-xs text-slate-500 ml-1">{value}/5</span>}
      </div>
    </div>
  );
}

// ── Review form ───────────────────────────────────────────────────────────────
function ReviewForm({ hostingId, onSuccess, t }) {
  const { authHeaders } = useUserAuth();
  const [form, setForm] = useState({
    title: '', comment: '', stars: 0,
    stars_price: 0, stars_support: 0, stars_speed: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stars) { setError('Bitte wähle eine Gesamtbewertung.'); return; }
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        hosting_id: hostingId,
        title: form.title,
        comment: form.comment,
        stars: form.stars,
        stars_price:   form.stars_price   || null,
        stars_support: form.stars_support || null,
        stars_speed:   form.stars_speed   || null,
      }, { headers: authHeaders() });
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(d => d.msg).join(' ') : (detail || 'Fehler'));
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-4 p-4 bg-slate-900/40 border border-slate-800 rounded-xl"
      data-testid="review-form">
      <h3 className="text-sm font-semibold text-white">{t.reviews.formTitle}</h3>

      {error && (
        <div data-testid="review-error"
          className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">
          {error}
        </div>
      )}

      {/* Star pickers */}
      <div className="space-y-2.5">
        <StarPicker value={form.stars}         onChange={v => setForm(f => ({...f, stars: v}))}         label={t.reviews.overallStars + ' *'} />
        <StarPicker value={form.stars_price}   onChange={v => setForm(f => ({...f, stars_price: v}))}   label={t.reviews.priceStars} />
        <StarPicker value={form.stars_support} onChange={v => setForm(f => ({...f, stars_support: v}))} label={t.reviews.supportStars} />
        <StarPicker value={form.stars_speed}   onChange={v => setForm(f => ({...f, stars_speed: v}))}   label={t.reviews.speedStars} />
      </div>

      {/* Title */}
      <div>
        <label className="text-xs text-slate-400 uppercase tracking-wider font-medium block mb-1">
          {t.reviews.titleLabel}
        </label>
        <input type="text" value={form.title}
          onChange={e => setForm(f => ({...f, title: e.target.value}))}
          placeholder={t.reviews.titlePlaceholder}
          data-testid="review-title"
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors" />
      </div>

      {/* Comment */}
      <div>
        <label className="text-xs text-slate-400 uppercase tracking-wider font-medium block mb-1">
          {t.reviews.commentLabel} *
        </label>
        <textarea required value={form.comment}
          onChange={e => setForm(f => ({...f, comment: e.target.value}))}
          placeholder={t.reviews.commentPlaceholder}
          data-testid="review-comment"
          rows={4}
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors resize-none" />
      </div>

      <button type="submit" disabled={loading} data-testid="review-submit-btn"
        className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-slate-950 font-semibold rounded-xl hover:bg-cyan-400 transition-colors disabled:opacity-50 text-sm">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {loading ? t.reviews.submitting : t.reviews.submit}
      </button>
    </form>
  );
}

// ── Reply form for provider ───────────────────────────────────────────────────
function ReplyForm({ reviewId, existing, onSave, t, authHeaders }) {
  const [text, setText] = useState(existing || '');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const save = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/provider/reviews/${reviewId}/reply`,
        { reply_text: text }, { headers: authHeaders() });
      onSave(text);
      setOpen(false);
    } finally { setLoading(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="text-xs text-cyan-400 hover:text-cyan-300 mt-2 flex items-center gap-1">
      <MessageSquare className="w-3.5 h-3.5" />
      {existing ? 'Antwort bearbeiten' : t.reviews.replyLabel}
    </button>
  );

  return (
    <div className="mt-3 space-y-2">
      <textarea value={text} onChange={e => setText(e.target.value)}
        placeholder={t.reviews.replyPlaceholder} rows={3}
        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors resize-none" />
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={loading || !text.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 text-xs">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {t.reviews.saveReply}
        </button>
        <button onClick={() => setOpen(false)}
          className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

// ── Single review card ────────────────────────────────────────────────────────
function ReviewCard({ review, isProvider, t, authHeaders, onReplyUpdate }) {
  const [localReply, setLocalReply] = useState(review.reply_text);
  const initials = review.display_name?.slice(0,2).toUpperCase() || '?';

  return (
    <div data-testid={`review-card-${review.id}`}
      className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-xs font-bold text-cyan-400 shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white">{review.display_name}</span>
              <StarDisplay value={review.stars} size="sm" />
            </div>
            <span className="text-xs text-slate-600">{review.created_at?.slice(0,10)}</span>
          </div>

          {/* Category ratings */}
          {(review.stars_price || review.stars_support || review.stars_speed) && (
            <div className="flex flex-wrap gap-2 mt-1.5">
              {review.stars_price   && <span className="text-xs text-slate-500">Preis <span className="text-amber-400">{'★'.repeat(review.stars_price)}</span></span>}
              {review.stars_support && <span className="text-xs text-slate-500">Support <span className="text-amber-400">{'★'.repeat(review.stars_support)}</span></span>}
              {review.stars_speed   && <span className="text-xs text-slate-500">Perf. <span className="text-amber-400">{'★'.repeat(review.stars_speed)}</span></span>}
            </div>
          )}

          {review.title && <p className="text-sm font-semibold text-white mt-2">{review.title}</p>}
          <p className="text-sm text-slate-300 mt-1 leading-relaxed">{review.comment}</p>

          {/* Provider reply */}
          {localReply ? (
            <div className="mt-3 ml-2 pl-3 border-l-2 border-cyan-500/30">
              <p className="text-xs font-semibold text-cyan-400 mb-1">{t.reviews.providerReply}</p>
              <p className="text-sm text-slate-400 leading-relaxed">{localReply}</p>
              {isProvider && (
                <ReplyForm reviewId={review.id} existing={localReply}
                  onSave={text => { setLocalReply(text); onReplyUpdate?.(); }}
                  t={t} authHeaders={authHeaders} />
              )}
            </div>
          ) : isProvider ? (
            <ReplyForm reviewId={review.id}
              onSave={text => { setLocalReply(text); onReplyUpdate?.(); }}
              t={t} authHeaders={authHeaders} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ── Average rating summary ────────────────────────────────────────────────────
function RatingSummary({ reviews, t }) {
  const avg = reviews.reduce((s, r) => s + r.stars, 0) / reviews.length;
  const avgPrice   = reviews.filter(r=>r.stars_price).reduce((s,r)=>s+r.stars_price,0) / (reviews.filter(r=>r.stars_price).length || 1);
  const avgSupport = reviews.filter(r=>r.stars_support).reduce((s,r)=>s+r.stars_support,0) / (reviews.filter(r=>r.stars_support).length || 1);
  const avgSpeed   = reviews.filter(r=>r.stars_speed).reduce((s,r)=>s+r.stars_speed,0) / (reviews.filter(r=>r.stars_speed).length || 1);
  const hasCats = reviews.some(r => r.stars_price || r.stars_support || r.stars_speed);

  return (
    <div className="flex items-center gap-6 flex-wrap" data-testid="rating-summary">
      <div className="text-center">
        <p className="text-4xl font-bold text-white">{avg.toFixed(1)}</p>
        <StarDisplay value={avg} size="md" />
        <p className="text-xs text-slate-500 mt-1">{t.reviews.basedOn(reviews.length)}</p>
      </div>
      {hasCats && (
        <div className="space-y-1.5 text-xs text-slate-400">
          {reviews.some(r=>r.stars_price)   && <div className="flex items-center gap-2">{t.reviews.priceStars}   <StarDisplay value={avgPrice}   size="sm" /></div>}
          {reviews.some(r=>r.stars_support) && <div className="flex items-center gap-2">{t.reviews.supportStars} <StarDisplay value={avgSupport} size="sm" /></div>}
          {reviews.some(r=>r.stars_speed)   && <div className="flex items-center gap-2">{t.reviews.speedStars}   <StarDisplay value={avgSpeed}   size="sm" /></div>}
        </div>
      )}
    </div>
  );
}

// ── Main ReviewSection ────────────────────────────────────────────────────────
export function ReviewSection({ hostingId, isProvider = false, providerAuthHeaders = null }) {
  const { t } = useLanguage();
  const { user, loading: authLoading, authHeaders: communityAuthHeaders } = useUserAuth();
  // If providerAuthHeaders prop is passed (from ProviderReviews), use it; else use community auth
  const authHeaders = providerAuthHeaders || communityAuthHeaders;
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);

  const load = useCallback(async () => {
    try {
      const endpoint = isProvider
        ? `${API}/provider/reviews`
        : `${API}/hostings/${hostingId}/reviews`;
      const headers = isProvider ? authHeaders() : {};
      const { data } = await axios.get(endpoint, { headers });
      setReviews(data);
    } finally { setLoading(false); }
  }, [hostingId, isProvider, authHeaders]);

  // Check if logged-in user already reviewed this provider
  useEffect(() => {
    if (!user || isProvider || !hostingId) return;
    axios.get(`${API}/user/reviews`, { headers: communityAuthHeaders() })
      .then(r => setAlreadyReviewed(r.data.some(rv => rv.hosting_id === hostingId)))
      .catch(() => {});
  }, [user, hostingId, isProvider, communityAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleSuccess = () => {
    setSubmitted(true);
    setShowForm(false);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-6" data-testid="review-section">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-cyan-400" />
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
            {t.reviews.title}
          </h2>
          {reviews.length > 0 && (
            <span className="px-1.5 py-0.5 bg-slate-800 rounded text-xs text-slate-500">{reviews.length}</span>
          )}
        </div>

        {/* Write review button or already-reviewed badge */}
        {!isProvider && !authLoading && user && !showForm && !submitted && (
          alreadyReviewed ? (
            <span data-testid="already-reviewed-badge"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-xl text-xs font-medium">
              <Star className="w-3.5 h-3.5 fill-green-400" /> {t.reviews.alreadyReviewed}
            </span>
          ) : (
            <button onClick={() => setShowForm(true)} data-testid="write-review-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-xl hover:bg-cyan-500/20 transition-colors text-xs font-medium">
              <Star className="w-3.5 h-3.5" /> {t.reviews.writeReview}
            </button>
          )
        )}
      </div>

      {/* Success message */}
      {submitted && (
        <div data-testid="review-success"
          className="mb-4 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-xl text-sm text-green-400">
          {t.reviews.successMsg}
        </div>
      )}

      {/* Review form for logged-in users */}
      {!isProvider && showForm && (
        <ReviewForm hostingId={hostingId} onSuccess={handleSuccess} t={t} />
      )}

      {/* Login CTA */}
      {!isProvider && !authLoading && !user && (
        <div className="mb-5 px-4 py-4 bg-slate-900/60 border border-slate-700/50 rounded-xl flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-slate-400">{t.reviews.loginToReview}</p>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="review-login-btn"
              className="px-3 py-1.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg text-xs hover:bg-cyan-400 transition-colors">
              {t.reviews.loginCta}
            </Link>
            <span className="text-xs text-slate-600">{t.reviews.orCreate}</span>
            <Link to="/register"
              className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-600 transition-colors">
              {t.reviews.registerCta}
            </Link>
          </div>
        </div>
      )}

      {/* Reviews list */}
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-slate-500 py-4">{t.reviews.noReviews}</p>
      ) : (
        <div className="space-y-4">
          <RatingSummary reviews={reviews} t={t} />
          <div className="border-t border-slate-800 pt-4 space-y-3">
            {reviews.map(r => (
              <ReviewCard key={r.id} review={r} isProvider={isProvider}
                t={t} authHeaders={authHeaders} onReplyUpdate={load} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
